import {
  Heading,
  Icon,
  Image,
  InlineStack,
  List,
  ListItem,
  Paragraph,
  PlainButton,
  Stack,
  Strong,
  Text,
} from '@customerio/pluma-components/react';
import { isEmptyString } from '@/utils/string';
import { fillTemplateVariables, type ContentTemplateView } from '../utils/twilio-content-template';
import * as styles from './delivery-message-preview.css';

type SmsContentTemplateProps = { view: ContentTemplateView };

/** A Twilio content template sent over WhatsApp (Ember `SmsPreview` with `@isWhatsapp`): a card, or its variables when the type has no card. */
export function SmsContentTemplate({ view }: SmsContentTemplateProps) {
  if (view.kind === 'variables') {
    return <ContentTemplateVariables variables={view.variables} />;
  }
  return <ContentTemplateCard view={view} />;
}

type ContentTemplateVariablesProps = { variables: { identifier: string; value: string }[] };

function ContentTemplateVariables({ variables }: ContentTemplateVariablesProps) {
  return (
    <Stack gap="100" p="125">
      <Paragraph data-test-content-template-placeholder>
        We are currently unable to render a preview for this content template. Here are how the variables will appear:
      </Paragraph>
      <List gap="100">
        {variables.length === 0 ? (
          <ListItem data-test-content-template-no-variables>This content template has no variables.</ListItem>
        ) : (
          variables.map(({ identifier, value }) => (
            <ListItem key={identifier}>
              <Strong as="b">{identifier}: </Strong>
              {value}
            </ListItem>
          ))
        )}
      </List>
    </Stack>
  );
}

type ContentTemplateCardProps = { view: Extract<ContentTemplateView, { kind: 'card' }> };

function ContentTemplateCard({ view }: ContentTemplateCardProps) {
  const { detail, variables } = view;

  return (
    <>
      <Stack gap="100" p="125">
        <TemplateText field="header" text={detail.header_text} variables={variables} />
        <TemplateText field="body" text={detail.body} variables={variables} />
        {detail.media?.map((src) => (
          <Image key={src} alt="Preview of media from a twilio content template" src={src} />
        ))}
        {/* Twilio card templates carry a title and subtitle instead of header text and body. */}
        <TemplateText field="title" text={detail.title} variables={variables} />
        <TemplateText field="subtitle" text={detail.subtitle} variables={variables} />
        <TemplateFooter footer={detail.footer} />
      </Stack>
      {detail.actions.map((action, index) => (
        <PlainButton key={index} className={styles.smsButton} data-test-content-template-action>
          <InlineStack alignInline="center" alignBlock="center" gap="100">
            <Icon name="link" />
            <TemplateText field="action" text={action.title} variables={variables} />
          </InlineStack>
        </PlainButton>
      ))}
    </>
  );
}

type TemplateFooterProps = { footer: string | undefined };

function TemplateFooter({ footer }: TemplateFooterProps) {
  if (isEmptyString(footer)) return null;
  return (
    <Text size="xs" color="disabled" data-test-content-template-footer>
      {footer}
    </Text>
  );
}

type TemplateTextProps = {
  field: 'header' | 'body' | 'title' | 'subtitle' | 'action';
  text: string | undefined;
  variables: { identifier: string; value: string }[];
};

/** One text field of the card, with the template's variables filled in. Nothing renders for an empty field. */
function TemplateText({ field, text, variables }: TemplateTextProps) {
  if (isEmptyString(text)) return null;
  // Sanitized (relaxed allowlist) after the variables are filled in.
  const html = { __html: fillTemplateVariables(text ?? '', variables) };

  /* nosemgrep: react-dangerouslysetinnerhtml — sanitized via DOMPurify in fillTemplateVariables() */
  switch (field) {
    case 'header':
      return <Text size="md" weight="bold" data-test-content-template-header-text dangerouslySetInnerHTML={html} />;
    case 'body':
      return <div className={styles.smsCardBody} data-test-content-template-body dangerouslySetInnerHTML={html} />;
    case 'title':
      return <Heading level="1" dangerouslySetInnerHTML={html} />;
    case 'subtitle':
      return <Paragraph dangerouslySetInnerHTML={html} />;
    case 'action':
      return <span dangerouslySetInnerHTML={html} />;
    default: {
      const _exhaustive: never = field;
      return _exhaustive;
    }
  }
}
